import { zodResolver } from "@hookform/resolvers/zod";
import {
  AnalyticEvent,
  isWeb,
  phoneWithCountryCode,
  useCheckIfProfileExistsLazyQuery,
  useGetUserProfileLazyQuery,
  usePostHog,
} from "@stepapp/core";
import { RegisterTypeEnum } from "@stepapp/core/apiTypes";
import { Block, Button, palette, spacing, useHeaderHeight } from "@stepapp/ui";
import { Slot, Stack, useGlobalSearchParams, useRouter, useSegments } from "expo-router";
import { useCallback, useEffect, useMemo } from "react";
import { FormProvider, type Resolver, useForm, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { KeyboardAvoidingView, Platform, ScrollView, StyleSheet } from "react-native";
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from "react-native-reanimated";
import { loaderRef } from "../../src/components";
import { useAppDispatch, useSignUp } from "../../src/hooks";
import useSendSMSCode from "../../src/hooks/useSMSCodeSend";
import { useRegisterFormSchema } from "../../src/schemas";
import { AppStorage } from "../../src/services/AppStorage";
import { setUserData } from "../../src/store/user";

export type RegisterFormValues = {
  code: string;
  email: string;
  firstName: string;
  lastName: string;
  otpCodeVerified: boolean;
  phoneNumber: string;
};

export const useRegisterFormContext = () => useFormContext<RegisterFormValues>();

const defaultValues = {
  email: "",
  firstName: "",
  lastName: "",
  code: "",
  otpCodeVerified: false,
  phoneNumber: "",
};

const fields = {
  phone: ["phoneNumber"],
  otp: ["otpCodeVerified"],
  "basic-info": ["firstName", "lastName", "email"],
};

const PROGRESS_MAX_WIDTH = spacing.unit * 15;

const authSegments = ["phone", "otp", "basic-info"] as const;
type AuthSegment = "phone" | "otp" | "basic-info";

export const useAuthTypeSegments = () => {
  const { t } = useTranslation();
  const { isRegister } = useGlobalSearchParams<{ isRegister: string }>();
  const segments = useSegments();
  const { navigate, replace } = useRouter();

  // @ts-expect-error Special case
  const currentSegment = (segments[1] as AuthSegment) || authSegments[0];
  const currentSegmentIndex = authSegments?.indexOf(currentSegment);

  const currentSegmentButtonText = useMemo(() => {
    switch (currentSegment) {
      case "basic-info":
        return t("screens.auth.register");

      default:
        return t("common.continue");
    }
  }, [currentSegment, t]);

  const goToNextPage = useCallback(
    (isRegister?: boolean) => {
      const nextSegment = authSegments?.[currentSegmentIndex + 1];

      if (nextSegment) {
        navigate({
          pathname: `/login/${nextSegment}`,
          params: { isRegister: `${!!isRegister}` },
        });
      } else {
        replace("/");
      }
    },
    [navigate, replace, currentSegmentIndex],
  );

  return useMemo(() => {
    return {
      isRegister,
      totalPages: authSegments?.length || 1,
      authSegments,
      currentSegment,
      currentSegmentIndex,
      goToNextPage,
      currentSegmentButtonText,
    };
  }, [currentSegmentButtonText, isRegister, goToNextPage, currentSegment, currentSegmentIndex]);
};

export default function RegisterLayout() {
  const postHog = usePostHog();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { replace } = useRouter();
  const sendSMSCode = useSendSMSCode();
  const signUp = useSignUp();

  const [checkIfProfileExists] = useCheckIfProfileExistsLazyQuery();
  const [getUserProfile] = useGetUserProfileLazyQuery({
    variables: {
      isCustomerProfile: true,
    },
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  const {
    currentSegmentButtonText,
    goToNextPage,
    isRegister,
    totalPages,
    currentSegment,
    currentSegmentIndex,
  } = useAuthTypeSegments();

  const defaultSelectedPage = currentSegmentIndex || 0;

  const validateRegister = isRegister && currentSegment === "basic-info";

  const registerSchema = useRegisterFormSchema({
    isRegister: !!validateRegister,
  });

  const methods = useForm({
    mode: "onChange",
    reValidateMode: "onChange",
    resolver: zodResolver(registerSchema) as Resolver<RegisterFormValues>,
    defaultValues,
  });

  const progress = useSharedValue((PROGRESS_MAX_WIDTH * (defaultSelectedPage + 1)) / totalPages);

  const {
    handleSubmit,
    watch,
    formState: { errors, touchedFields, isSubmitting },
  } = methods;

  const values = watch();

  useEffect(() => {
    progress.value = withTiming((PROGRESS_MAX_WIDTH * (currentSegmentIndex + 1)) / totalPages, {
      duration: 300,
      easing: Easing.inOut(Easing.ease),
    });
  }, [progress, currentSegmentIndex, totalPages]);

  useEffect(() => {
    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: todo
    const fetchData = async () => {
      const { phoneNumber, otpCodeVerified, code } = values;

      if (phoneNumber && otpCodeVerified && code) {
        methods.setValue("otpCodeVerified", false);

        try {
          const { data: profileExists } = await checkIfProfileExists();

          const newUser = !profileExists?.checkIfProfileExists;

          if (newUser) {
            postHog?.capture(AnalyticEvent.Register.Details);
            goToNextPage(newUser);
          } else {
            const { data: getUserData } = await getUserProfile();
            if (getUserData?.getMyProfile) {
              dispatch(setUserData(getUserData.getMyProfile));
              const {
                id,
                profileInformation: { firstName, lastName, email, phone },
              } = getUserData.getMyProfile;

              postHog?.identify(id, {
                platform: Platform.OS,
                name: `${firstName} ${lastName}`,
                email,
                phoneNumber: phone,
              });
            }
            replace("/");
          }
        } catch (_error) {
        } finally {
          loaderRef.current?.hideLoader();
        }
      }
    };

    fetchData();
  }, [
    values,
    values.code,
    values.otpCodeVerified,
    values.phoneNumber,
    replace,
    goToNextPage,
    dispatch,
    checkIfProfileExists,
    getUserProfile,
    methods,
    postHog,
  ]);

  const handleSendSMSCode = useCallback(async () => {
    loaderRef.current?.showLoader();
    const phoneNumber = phoneWithCountryCode(values.phoneNumber, AppStorage().get("countryCode"));

    const { success, isRegister, error } = await sendSMSCode({
      phoneNumber,
    });

    if (success) {
      goToNextPage(isRegister);
      loaderRef.current?.hideLoader();
    } else {
      loaderRef.current?.hideLoader();
      return alert(error || t("common.error"));
    }
  }, [goToNextPage, sendSMSCode, values.phoneNumber, t]);

  const register = handleSubmit(async (values) => {
    loaderRef.current?.showLoader();

    const { email, lastName, firstName, phoneNumber } = values;

    const successSignUp = await signUp({
      email,
      lastName,
      firstName,
      phoneNumber,
      registerType: RegisterTypeEnum.Email,
    });

    if (successSignUp) {
      goToNextPage();
    }

    loaderRef.current?.hideLoader();
  });

  const handlePress = useCallback(() => {
    if (isSubmitting) return;

    switch (currentSegment) {
      case "phone": {
        handleSendSMSCode();
        break;
      }

      case "otp":
        // submit handled on otp component
        break;

      case "basic-info": {
        register();
        break;
      }
    }
  }, [handleSendSMSCode, isSubmitting, currentSegment, register]);

  const pageErrors = (() => {
    const fieldNames = (fields?.[currentSegment as keyof typeof fields] ||
      []) as (keyof RegisterFormValues)[];

    const isPageTouched = fieldNames?.every((fieldName) => touchedFields?.[fieldName]);

    const pageErrors = fieldNames?.reduce((acc, fieldName) => {
      const error = errors?.[fieldName as keyof RegisterFormValues];

      return { ...acc, [fieldName]: error };
    }, {});

    return isPageTouched ? pageErrors : { disabled: true };
  })();

  const isButtonDisabled = Object.values(pageErrors).some((error) => !!error);
  const progressAnimatedStyle = useAnimatedStyle(() => ({
    width: progress.value,
  }));

  return (
    <FormProvider {...methods}>
      <KeyboardAvoidingView
        style={styles.container}
        contentContainerStyle={styles.contentContainer}
        behavior={Platform.select({ default: "padding", android: undefined })}
        keyboardVerticalOffset={useHeaderHeight() + spacing.unit * 4}
      >
        <ScrollView contentContainerStyle={styles.contentContainer}>
          <Block style={styles.progressBar}>
            <Animated.View style={[styles.progress, progressAnimatedStyle]} />
          </Block>

          {isWeb ? (
            <Slot />
          ) : (
            <Stack
              screenOptions={{
                headerShown: false,
                contentStyle: {
                  backgroundColor: palette.common.white,
                  paddingHorizontal: spacing.unit * 2.5,
                },
              }}
            >
              <Stack.Screen name="otp" options={{ headerShown: false }} />
              <Stack.Screen name="phone" options={{ headerShown: false }} />
              <Stack.Screen name="basic-info" options={{ headerShown: false }} />
            </Stack>
          )}

          <Button
            style={styles.footerButton}
            disabled={isButtonDisabled}
            label={currentSegmentButtonText}
            onPress={handlePress}
            type="primary"
          />
        </ScrollView>
      </KeyboardAvoidingView>
    </FormProvider>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    marginHorizontal: "auto",
    maxWidth: 580,
    width: "100%",
  },
  contentContainer: {
    flex: 1,
  },
  footerButton: {
    flex: isWeb ? 1 : 0,
    paddingBottom: spacing.unit * 2,
    marginTop: isWeb ? spacing.unit * 2 : 0,
    paddingHorizontal: spacing.unit * (isWeb ? 0 : 2.5),
    zIndex: 5,
  },
  progress: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: palette.primary.accent,
    borderRadius: 5,
  },
  progressBar: {
    backgroundColor: palette.font.placeholder,
    borderRadius: 5,
    height: spacing.unit * 1.5,
    marginHorizontal: spacing.unit * (isWeb ? 0 : 2.5),
    marginBottom: spacing.unit,
    width: spacing.unit * 15,
  },
  tabsStyles: {
    backgroundColor: palette.common.white,
  },
});
